/**
 * HLOS-168-B: TS wrapper que comunica com o `IOSVaultPlugin.swift` via
 * `window.webkit.messageHandlers.hlos.postMessage()` (mesmo canal do
 * `apple_llm_bridge.ts`).
 *
 * O TS-side (`vault_bridge.ts`) e o Swift-side (`AppleLLMRuntimeBridge.swift`
 * estendido em HLOS-168-A) compartilham o mesmo canal `hlos` mas são
 * distinguidos pelo campo `method` da mensagem:
 *   - llm.* → AppleLLMRuntime (Foundation Models)
 *   - vaultRead/Write/List/MigrateFromOPFS → IOSVaultPlugin (storage local iOS)
 *
 * Bridge APENAS transporta bytes JÁ cifrados (vault-crypto skill).
 * Master key nunca trafega por este canal (CL-08 zero key escrow).
 *
 * Path conventions (espelham `IOSVaultPlugin.absoluteURL`):
 *   - relativePath = "hlos_primary/vault.json"
 *   - resolve para Documents/hlos/vault/hlos_primary/vault.json
 *
 * HLOS-168-B.12: usa hlosRouter para receber responses (em vez de instalar
 * o próprio global `__hlos_handleMessage__`, que colidia com apple_llm_bridge).
 */

import { Capacitor } from "@capacitor/core";
import { registerVaultHandler, installHlosRouterOnce } from "./hlosRouter";

/** Default timeout (matches AppleLLMRuntime DEFAULT_TIMEOUT_MS).
 * HLOS-168-B.11: aumentado de 2_000 para 5_000ms porque no iOS 26+ a
 * latência de evaluateJavaScript + dispatch é maior. O apple_llm_bridge
 * também usa 5s.
 */
const DEFAULT_TIMEOUT_MS = 5_000;

/**
 * HLOS-168-B: true if running on iOS native (Capacitor platform = "ios").
 * Duplicates the check in `apple_llm_bridge.ts` to keep this module standalone
 * (avoids changing that file's export surface for a TS-only addition).
 */
export function isApplePlatform(): boolean {
  return (
    typeof window !== "undefined" &&
    Capacitor.isNativePlatform() &&
    Capacitor.getPlatform() === "ios"
  );
}

/** Underlying iOS message shape posted to `window.webkit.messageHandlers.hlos`. */
type VaultMessage = {
  id: string;
  // HLOS-168-B.9: chave "method" (não "op") para casar com o dispatch Swift
  // em `AppleLLMRuntimeBridge.dispatch(method:body:)` que faz switch em
  // `case "vaultRead" | "vaultWrite" | "vaultList" | "vaultMigrateFromOPFS"`.
  // Versão original usava `op: "vault.read" | ...` que não batia com o
  // `guard let method = body["method"]` do Swift → timeout silencioso.
  method: "vaultRead" | "vaultWrite" | "vaultList" | "vaultMigrateFromOPFS";
  // method-specific payload (kept loose for forward-compat with Swift additions)
  [key: string]: unknown;
};

/** Response shape returned by Swift. */
type VaultResponse =
  | {
      id: string;
      ok: true;
      result: string | string[] | null; // base64 string for read, array for list, null for "not found"
    }
  | {
      id: string;
      ok: false;
      error: string;
    };

/** Pending request tracked by id. */
interface PendingRequest {
  resolve: (value: string | string[] | null) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  method: VaultMessage["method"];
}

/** Public surface exposed to `storageFactory()`. */
export interface IOSVaultBridge {
  /** Read file from vault. Returns base64 string or null if not found. */
  read(relativePath: string): Promise<string | null>;
  /** Write bytes to vault. Returns true on success. */
  write(relativePath: string, base64Data: string): Promise<boolean>;
  /** List paths in directory. Returns array of relative paths. */
  list(directory?: string): Promise<string[]>;
  /** Migrate file from OPFS legacy storage (one-shot, on first iOS boot). */
  migrateFromOPFS(
    userId: string,
    opfsRelativePath: string,
    base64Data: string,
  ): Promise<boolean>;
}

/** Timeout error when Swift doesn't respond in time. */
class VaultTimeout extends Error {
  constructor(method: string) {
    super(`IOSVaultBridge.${method}() timed out after ${DEFAULT_TIMEOUT_MS}ms`);
    this.name = "IOSVaultBridgeTimeout";
  }
}

/**
 * True if the iOS Swift bridge is available.
 * Equivalent to `isAppleLLMRuntimeAvailable()` — same channel.
 */
export function isIOSVaultBridgeAvailable(): boolean {
  if (typeof window === "undefined") return false;
  // HLOS-168-B: only register if iOS native. Android/web use OPFS / Memory.
  if (!isApplePlatform()) return false;
  return getIOSBridge() !== null;
}

/**
 * Lazy-installed vault bridge. First call to `install()` wires the
  * `__hlos_handleMessage__` global for vault responses.
 */
const pending = new Map<string, PendingRequest>();
let counter = 0;

function nextId(): string {
  counter += 1;
  return `hlv_${Date.now()}_${counter}`;
}

function getIOSBridge():
  | { postMessage: (msg: VaultMessage) => void }
  | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    webkit?: {
      messageHandlers?: {
        hlos?: { postMessage: (msg: VaultMessage) => void };
      };
    };
  };
  const h = w.webkit?.messageHandlers?.hlos;
  if (!h || typeof h.postMessage !== "function") return null;
  return h;
}

function installResponseListener(): void {
  // HLOS-168-B.12: Router compartilhado (hlosRouter.ts) — dispatch por
  // prefixo de id (hlv_ vs hlm_). vault_bridge.ts não instala mais o
  // global direto (causava colisão com apple_llm_bridge.ts).
  installHlosRouterOnce();
}

/**
 * Install the bridge. Idempotent. Returns the bridge singleton.
 * Returns null if iOS native is not available (e.g., web, Android).
 */
export function installIOSVaultBridge(): IOSVaultBridge | null {
  if (typeof window === "undefined") return null;
  if (!isApplePlatform()) return null;

  const handler = getIOSBridge();
  if (!handler) return null;

  installResponseListener();

  const call = <T>(method: VaultMessage["method"], payload: Record<string, unknown>): Promise<T> => {
    const id = nextId();
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        pending.delete(id);
        reject(new VaultTimeout(method));
      }, DEFAULT_TIMEOUT_MS);
      pending.set(id, {
        resolve: resolve as (v: unknown) => void,
        reject,
        timer,
        method,
      });
      // HLOS-168-B.12: registrar handler no router compartilhado
      // (despacha por prefixo hlv_ vs hlm_).
      registerVaultHandler(id, (raw: unknown) => {
        if (!raw || typeof raw !== "object") {
          return;
        }
        const response = raw as VaultResponse;
        const p = pending.get(response.id);
        if (!p) return;
        pending.delete(response.id);
        clearTimeout(p.timer);
        if (response.ok) {
          p.resolve(response.result);
        } else {
          p.reject(new Error(response.error ?? "IOSVaultBridge Swift error"));
        }
      });
      handler.postMessage({ id, method, ...payload } as VaultMessage);
    });
  };

  return {
    read: (relativePath) =>
      call<string | null>("vaultRead", { path: relativePath }),
    write: (relativePath, base64Data) =>
      call<boolean>("vaultWrite", { path: relativePath, data: base64Data }),
    list: (directory = "") =>
      call<string[]>("vaultList", { directory }),
    migrateFromOPFS: (userId, opfsRelativePath, base64Data) =>
      call<boolean>("vaultMigrateFromOPFS", {
        userId,
        opfsPath: opfsRelativePath,
        data: base64Data,
      }),
  };
}
